import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Palette, X } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import BusinessLogoUpload from '../../components/BusinessLogoUpload';
import BrandFontPicker from '../../components/BrandFontPicker';
import {
  EMPTY_BRAND_GUIDELINES,
  getBrandGuidelines,
  hasAnyBrandGuidelines,
  normalizeBrandUrl,
  resolveDefaultOnlineBookingUrl,
  upsertBrandGuidelines,
  type BrandGuidelinesInput,
} from '../../lib/brandGuidelines';
import toast from 'react-hot-toast';

function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="mb-1.5">
      <label className="block text-[13px] font-semibold text-gray-800">{children}</label>
      {hint ? <p className="text-[12px] text-gray-500 mt-0.5">{hint}</p> : null}
    </div>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3.5 py-2.5 rounded-[10px] border border-gray-200 bg-white text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
      style={{ fontSize: 16 }}
    />
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3.5 py-2.5 rounded-[10px] border border-gray-200 bg-white text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-y min-h-[88px]"
      style={{ fontSize: 16 }}
    />
  );
}

function isHexColor(value: string): boolean {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
}

function toPickerHex(value: string): string {
  const v = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    const [, a, b, c] = v;
    return `#${a}${a}${b}${b}${c}${c}`;
  }
  // Native <input type="color"> requires a value; this is display-only until the user picks.
  return '#808080';
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const pickerRef = React.useRef<HTMLInputElement>(null);
  const hasColor = isHexColor(value);
  const display = value.trim();

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <span className="text-[13px] font-semibold text-gray-800">{label}</span>
        {hasColor || display ? (
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-[11px] font-semibold text-gray-400 hover:text-gray-700 touch-manipulation"
            aria-label={`Clear ${label}`}
          >
            Clear
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => pickerRef.current?.click()}
        className={`relative w-full aspect-square max-h-[88px] rounded-[12px] border touch-manipulation overflow-hidden ${
          hasColor
            ? 'border-gray-200 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]'
            : 'border-dashed border-gray-300 bg-[repeating-conic-gradient(#e5e7eb_0_25%,#fff_0_50%)] bg-[length:10px_10px]'
        }`}
        style={hasColor ? { backgroundColor: display } : undefined}
        aria-label={hasColor ? `${label}: ${display}` : `Pick ${label.toLowerCase()} color`}
        title={hasColor ? 'Change color' : 'Pick color'}
      >
        {!hasColor ? (
          <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-gray-400">
            Pick
          </span>
        ) : null}
      </button>
      <input
        ref={pickerRef}
        type="color"
        value={toPickerHex(value)}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
      />
      <input
        type="text"
        value={display}
        onChange={(e) => onChange(e.target.value)}
        placeholder="None"
        spellCheck={false}
        autoCapitalize="characters"
        className="mt-1.5 w-full px-2 py-1.5 rounded-lg border border-gray-200 bg-white text-center text-[11px] font-mono text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
        aria-label={`${label} color code`}
      />
    </div>
  );
}

type BrandGuidelinesProps = {
  /** Rendered inside a modal (no page padding, no outer card). */
  embedded?: boolean;
  /** Shown as an X in the header when embedded. */
  onClose?: () => void;
  /** Fires with the current form whenever it changes (after load and on every edit). */
  onFormChange?: (form: BrandGuidelinesInput) => void;
};

export default function BrandGuidelines({ embedded = false, onClose, onFormChange }: BrandGuidelinesProps = {}) {
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<BrandGuidelinesInput>({ ...EMPTY_BRAND_GUIDELINES });
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const hydratedRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastErrorToastRef = useRef<string | null>(null);
  const formRef = useRef(form);
  formRef.current = form;

  const filled = useMemo(() => hasAnyBrandGuidelines(form), [form]);

  const onFormChangeRef = useRef(onFormChange);
  onFormChangeRef.current = onFormChange;
  useEffect(() => {
    if (loading) return;
    onFormChangeRef.current?.(form);
  }, [form, loading]);

  const setField = useCallback(<K extends keyof BrandGuidelinesInput>(key: K, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    hydratedRef.current = false;
    setSaveStatus('idle');
    const load = async () => {
      if (!selectedBusiness?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const row = await getBrandGuidelines(selectedBusiness.id);
        if (cancelled) return;

        const next: BrandGuidelinesInput = row
          ? {
              header_color: row.header_color || '',
              button_color: row.button_color || '',
              primary_color: row.primary_color || '',
              secondary_color: row.secondary_color || '',
              accent_color: row.accent_color || '',
              display_font: row.display_font || '',
              body_font: row.body_font || '',
              tagline: row.tagline || '',
              voice_tone: row.voice_tone || '',
              dos: row.dos || '',
              donts: row.donts || '',
              preferred_words: row.preferred_words || '',
              avoided_words: row.avoided_words || '',
              freeform_notes: row.freeform_notes || '',
              website_url: row.website_url || '',
              online_booking_url: row.online_booking_url || '',
            }
          : { ...EMPTY_BRAND_GUIDELINES };

        const nexusUrl = normalizeBrandUrl(selectedBusiness.nexus_website_url);
        let appliedDefaults = false;
        if (!next.website_url && nexusUrl) {
          next.website_url = nexusUrl;
          appliedDefaults = true;
        }
        if (!next.online_booking_url) {
          const bookingUrl = await resolveDefaultOnlineBookingUrl(
            selectedBusiness.id,
            selectedBusiness.slug,
          );
          if (cancelled) return;
          if (bookingUrl) {
            next.online_booking_url = bookingUrl;
            appliedDefaults = true;
          }
        }

        setForm(next);

        // Persist Nexus / booking defaults so AI has them even before the user edits.
        if (appliedDefaults) {
          try {
            await upsertBrandGuidelines(selectedBusiness.id, next);
          } catch (err) {
            console.warn('Could not persist default brand link URLs:', err);
          }
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load brand guidelines');
      } finally {
        if (!cancelled) {
          setLoading(false);
          // Skip autosave for the hydrate write
          window.setTimeout(() => {
            if (!cancelled) hydratedRef.current = true;
          }, 0);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (savedClearTimerRef.current) clearTimeout(savedClearTimerRef.current);
    };
  }, [selectedBusiness?.id, selectedBusiness?.nexus_website_url, selectedBusiness?.slug]);

  useEffect(() => {
    if (!selectedBusiness?.id || !hydratedRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (savedClearTimerRef.current) clearTimeout(savedClearTimerRef.current);

    setSaveStatus('saving');
    saveTimerRef.current = setTimeout(() => {
      const businessId = selectedBusiness.id;
      const snapshot = formRef.current;
      void (async () => {
        try {
          await upsertBrandGuidelines(businessId, snapshot);
          setSaveStatus('saved');
          lastErrorToastRef.current = null;
          savedClearTimerRef.current = setTimeout(() => setSaveStatus('idle'), 1600);
        } catch (err) {
          console.error(err);
          setSaveStatus('error');
          const msg =
            err instanceof Error ? err.message : 'Failed to save brand guidelines';
          if (lastErrorToastRef.current !== msg) {
            lastErrorToastRef.current = msg;
            toast.error(msg);
          }
        }
      })();
    }, 550);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [form, selectedBusiness?.id]);

  if (loading) {
    return (
      <div className={embedded ? '' : 'p-4 sm:p-6'}>
        <div className={`bg-white p-8 flex items-center justify-center min-h-[240px] ${embedded ? '' : 'rounded-[12px] border border-gray-200/60 shadow-sm'}`}>
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={embedded ? '' : 'p-4 sm:p-6 max-w-3xl mx-auto'}
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      <div className={`bg-white ${embedded ? '' : 'overflow-hidden rounded-[12px] border border-gray-200/60 shadow-sm'}`}>
        <div className={`px-4 sm:px-6 py-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 ${embedded ? 'sticky top-0 z-10 bg-white/95 backdrop-blur' : ''}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="h-9 w-9 rounded-[10px] bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <Palette className="h-4 w-4" />
              </div>
              <h2
                className="text-xl font-bold text-gray-900"
                style={{ letterSpacing: '-0.02em' }}
              >
                Brand Guidelines
              </h2>
            </div>
            <p className="text-[13px] text-gray-500 sm:pl-[46px]">
              Changes save automatically. AI features use these guidelines when available —
              everything is optional.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 self-start sm:self-center">
            <div
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-semibold"
              aria-live="polite"
            >
              {saveStatus === 'saving' ? (
                <span className="inline-flex items-center gap-1.5 text-gray-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Saving…
                </span>
              ) : saveStatus === 'saved' ? (
                <span className="inline-flex items-center gap-1.5 text-green-600">
                  <Check className="h-3.5 w-3.5" />
                  Saved
                </span>
              ) : saveStatus === 'error' ? (
                <span className="text-red-600">Save failed</span>
              ) : (
                <span className="text-gray-400">Live</span>
              )}
            </div>
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200"
                aria-label="Close"
              >
                <X className="h-4 w-4" strokeWidth={2.25} />
              </button>
            ) : null}
          </div>
        </div>

        {!filled && (
          <div className="mx-4 sm:mx-6 mt-5 rounded-[10px] bg-blue-50/80 border border-blue-100 px-4 py-3 text-[13px] text-blue-800">
            No guidelines set yet. Add what matters for your brand — AI will only use fields you
            fill in.
          </div>
        )}

        <div className="p-4 sm:p-6 space-y-8">
          {/* Logo */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Logo
            </h3>
            <div className="p-4 rounded-[12px] bg-gray-50 border border-gray-200/60">
              <p className="text-[14px] font-semibold text-gray-900 mb-0.5">Central business logo</p>
              <p className="text-[12px] text-gray-500 mb-4">
                Used across the app, booking page, emails, and AI. Changes here update your
                business logo everywhere.
              </p>
              <BusinessLogoUpload
                size="large"
                showLabel={false}
                hint="PNG or JPG recommended · max 5MB · updates the central business logo"
                className="max-w-xs"
              />
            </div>
          </section>

          {/* Colors */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Colors
            </h3>
            <p className="text-[12px] text-gray-500 mb-3">
              Optional. Header for email/page top bars. Button for CTA buttons. Primary/secondary/accent
              for the rest of the brand — set only what you need.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5 sm:gap-3 max-w-2xl">
              <ColorField
                label="Header"
                value={form.header_color}
                onChange={(v) => setField('header_color', v)}
              />
              <ColorField
                label="Button"
                value={form.button_color}
                onChange={(v) => setField('button_color', v)}
              />
              <ColorField
                label="Primary"
                value={form.primary_color}
                onChange={(v) => setField('primary_color', v)}
              />
              <ColorField
                label="Secondary"
                value={form.secondary_color}
                onChange={(v) => setField('secondary_color', v)}
              />
              <ColorField
                label="Accent"
                value={form.accent_color}
                onChange={(v) => setField('accent_color', v)}
              />
            </div>
          </section>

          {/* Typography */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Typography
            </h3>
            <p className="text-[12px] text-gray-500 mb-3">
              Optional. Pick from system fonts or search the full Google Fonts catalog — AI will
              only use fonts you set.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <BrandFontPicker
                label="Display font"
                hint="Headlines and titles"
                value={form.display_font}
                onChange={(v) => setField('display_font', v)}
                placeholder="Select display font"
              />
              <BrandFontPicker
                label="Body font"
                hint="Paragraphs and body copy"
                value={form.body_font}
                onChange={(v) => setField('body_font', v)}
                placeholder="Select body font"
              />
            </div>
          </section>

          {/* Links */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Links
            </h3>
            <p className="text-[12px] text-gray-500 mb-3">
              AI uses these for CTAs and button links in emails and campaigns. Website defaults to
              your connected Nexus site when available.
            </p>
            <div className="space-y-4">
              <div>
                <FieldLabel
                  hint={
                    selectedBusiness?.nexus_website_url
                      ? 'Prefilled from your connected Nexus website — edit anytime'
                      : 'Your public marketing website'
                  }
                >
                  Website URL
                </FieldLabel>
                <TextInput
                  value={form.website_url}
                  onChange={(v) => setField('website_url', v)}
                  placeholder="https://www.yourbusiness.com"
                />
              </div>
              <div>
                <FieldLabel hint="Used for Book Now / Schedule CTAs">
                  Online Booking URL
                </FieldLabel>
                <TextInput
                  value={form.online_booking_url}
                  onChange={(v) => setField('online_booking_url', v)}
                  placeholder="https://yoursite.com/your-slug/book"
                />
              </div>
            </div>
          </section>

          {/* Voice */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Voice
            </h3>
            <div className="space-y-4">
              <div>
                <FieldLabel hint="One line that captures the brand">Tagline / description</FieldLabel>
                <TextInput
                  value={form.tagline}
                  onChange={(v) => setField('tagline', v)}
                  placeholder="Premium mobile detailing that comes to you"
                />
              </div>
              <div>
                <FieldLabel hint="How the brand should sound in writing">
                  Voice & tone
                </FieldLabel>
                <TextArea
                  value={form.voice_tone}
                  onChange={(v) => setField('voice_tone', v)}
                  placeholder="Friendly, confident, and concise. Never salesy or corporate."
                  rows={3}
                />
              </div>
            </div>
          </section>

          {/* Language rules */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Language rules
            </h3>
            <div className="space-y-4">
              <div>
                <FieldLabel>Do’s</FieldLabel>
                <TextArea
                  value={form.dos}
                  onChange={(v) => setField('dos', v)}
                  placeholder="Use first names. Emphasize convenience and quality. Mention local service area."
                />
              </div>
              <div>
                <FieldLabel>Don’ts</FieldLabel>
                <TextArea
                  value={form.donts}
                  onChange={(v) => setField('donts', v)}
                  placeholder="Don’t use slang. Don’t promise same-day unless confirmed. Avoid competitor names."
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FieldLabel hint="Comma-separated">Preferred words</FieldLabel>
                  <TextArea
                    value={form.preferred_words}
                    onChange={(v) => setField('preferred_words', v)}
                    placeholder="detail, protect, shine, booked"
                    rows={2}
                  />
                </div>
                <div>
                  <FieldLabel hint="Comma-separated">Words to avoid</FieldLabel>
                  <TextArea
                    value={form.avoided_words}
                    onChange={(v) => setField('avoided_words', v)}
                    placeholder="cheap, deal, discount frenzy"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Extra notes */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-3">
              Extra notes for AI
            </h3>
            <FieldLabel hint="Anything else AI writers should always know">
              Freeform guidelines
            </FieldLabel>
            <TextArea
              value={form.freeform_notes}
              onChange={(v) => setField('freeform_notes', v)}
              placeholder="We serve the greater metro area only. Always include a booking CTA. Seasonal offers run Fridays."
              rows={4}
            />
          </section>
        </div>
      </div>
    </div>
  );
}
